// The pallets on one PPECB consignment, inside its card on With PPECB (F03
// UF-04). The card used to say "12 pallets" and nothing more: no way to see
// which, and no way out to any of them.
//
//   - A pallet still on the inspection queue keeps its full card here, with
//     Approve / Reject — the hand verdict stays on a pallet with PPECB until
//     UF-20 takes it off. These come from the queue read, so a failed list
//     read never hides them.
//   - The consignment's other pallets (approved, dispatched, rejected — off
//     the queue) are read page by page while the list is open, one row each,
//     the number a link to the pallet. Past the first few, "+ N more pallets".
//   - PPECB's named validation errors follow. An error names its pallet, as
//     a link, only when PPECB's own text quotes that pallet's number or SSCC
//     (errorPallet): PPECB reports a line index, and the submission's line
//     order is not stored.
import { useState, type ReactNode } from 'react'
import { StatusPill } from '@/components/ui'
import { ChevronDown, ChevronUp } from 'lucide-react'
import WidgetRetry from '@/components/WidgetRetry'
import { readState } from '@/lib/readState'
import { errorPallet, pallets, type QueuePallet } from './inspectionQueue'
import { useConsignmentPallets, type ConsignmentPallet } from './inspectionReads'
import PalletLink from './PalletLink'

/** Rows shown before "+ N more pallets". */
export const LIST_FIRST = 8

export type ValidationErrorRow = { property?: string; message?: string }

function OtherRow({ p, canOpenPallet }: { p: ConsignmentPallet; canOpenPallet: boolean }) {
  const facts = [p.runs?.variety, p.grade, p.size_code].filter(Boolean).join(' · ') || '—'
  return (
    <li className="flex items-center gap-2 min-h-[44px] rounded-lg bg-surface/50 px-3 text-xs">
      <PalletLink id={p.pallet_id} canOpen={canOpenPallet} className="font-mono font-semibold text-fg" />
      <span className="flex-1 min-w-0 truncate text-fg-muted">
        {facts}{p.runs?.run_number ? ` · run ${p.runs.run_number}` : ''}
      </span>
      <StatusPill object="pallet" status={p.status} className="shrink-0" />
    </li>
  )
}

/** "Show / Hide the N pallets" — the list's one toggle. */
function ListToggle({ open, total, onToggle }: { open: boolean; total: number; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open}
      className="btn-ghost btn-sm min-h-[44px] flex items-center gap-1.5">
      {open ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
      {open ? 'Hide' : 'Show'} the {pallets(total)}
    </button>
  )
}

/** "+ N more pallets" past the first {@link LIST_FIRST} rows, or nothing. */
function ShowMore({ hidden, onShowAll }: { hidden: number; onShowAll: () => void }) {
  if (hidden <= 0) return null
  return (
    <button type="button" onClick={onShowAll} className="btn-ghost btn-sm min-h-[44px]">
      + {hidden} more pallet{hidden === 1 ? '' : 's'}
    </button>
  )
}

/** The open list: the queue pallets' cards, then the consignment's others. */
function OpenList({ onQueue, card, listQ, canOpenPallet, showAll, onShowAll }: {
  onQueue:       QueuePallet[]
  card:          (p: QueuePallet) => ReactNode
  listQ:         ReturnType<typeof useConsignmentPallets>
  canOpenPallet: boolean
  showAll:       boolean
  onShowAll:     () => void
}) {
  const state = readState(listQ)
  const queued  = new Set(onQueue.map(p => p.pallet_id))
  const others  = (listQ.data ?? []).filter(p => !queued.has(p.pallet_id))
  const visible = showAll ? others : others.slice(0, LIST_FIRST)
  return (
    <>
      {onQueue.length > 0 && <div className="space-y-2">{onQueue.map(card)}</div>}
      {state === 'loading' && <p className="text-xs text-muted">Reading the pallets on this consignment…</p>}
      {state === 'unread' && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-amber-300">
          Couldn't read the pallets on this consignment
          <WidgetRetry onRetry={() => { void listQ.refetch() }} busy={listQ.isFetching} />
        </div>
      )}
      {visible.length > 0 && (
        <ul className="space-y-1" aria-label="Pallets on this consignment">
          {visible.map(p => <OtherRow key={p.pallet_id} p={p} canOpenPallet={canOpenPallet} />)}
        </ul>
      )}
      <ShowMore hidden={others.length - visible.length} onShowAll={onShowAll} />
    </>
  )
}

/** One PPECB validation error — its pallet a link when PPECB's text quotes it.
 *  `overflow-wrap: anywhere` lets a long unbroken property path
 *  (`consignmentLines[1].consignmentLinePallets[0].ssccReference`) wrap
 *  instead of widening a phone's page. */
function ErrorLine({ v, named, canOpenPallet }: {
  v: ValidationErrorRow; named: ReadonlyArray<{ pallet_id: string; ssc_id?: string | null }>; canOpenPallet: boolean
}) {
  const id = errorPallet(v, named)
  return (
    <p className="text-xs text-red-200 [overflow-wrap:anywhere]">
      {id && <><PalletLink id={id} canOpen={canOpenPallet} className="font-mono font-semibold text-red-100" />{' · '}</>}
      {v.property ? <span className="font-mono">{v.property}: </span> : null}{v.message ?? ''}
    </p>
  )
}

export default function ConsignmentPallets({
  consignmentId, palletCount, onQueue, card, canOpenPallet, defaultOpen, errors,
}: {
  consignmentId: string
  /** The dashboard's count of pallets on the consignment. */
  palletCount:   number
  /** This consignment's pallets on the queue's With PPECB slice. */
  onQueue:       QueuePallet[]
  /** The page's QueueCard for a queue pallet (keyed). */
  card:          (p: QueuePallet) => ReactNode
  canOpenPallet: boolean
  /** Open unless the person closes it: a consignment awaiting PPECB, one with
   *  queue pallets or errors, or the one "Open it" pointed at. */
  defaultOpen:   boolean
  /** PPECB's named validation errors to show, or none. */
  errors:        ValidationErrorRow[] | null
}) {
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const [showAll, setShowAll]   = useState(false)
  const open = userOpen ?? defaultOpen
  const errorRows = errors ?? []
  // Errors read the list even while it is closed: an SSCC in PPECB's text
  // resolves to a pallet only against the consignment's own pallets. Until
  // they are in, the queue's pallets, by number.
  const listQ = useConsignmentPallets(consignmentId, open || errorRows.length > 0)
  const named: ReadonlyArray<{ pallet_id: string; ssc_id?: string | null }> = listQ.data ?? onQueue
  const total = Math.max(palletCount, onQueue.length)

  return (
    <>
      {total > 0 && (
        <div className="mt-2 space-y-2">
          <ListToggle open={open} total={total} onToggle={() => setUserOpen(!open)} />
          {open && (
            <OpenList onQueue={onQueue} card={card} listQ={listQ} canOpenPallet={canOpenPallet}
              showAll={showAll} onShowAll={() => setShowAll(true)} />
          )}
        </div>
      )}
      {errorRows.length > 0 && (
        <div className="mt-2 rounded-lg bg-red-900/30 border border-red-800 px-2.5 py-1.5">
          <p className="text-[10px] uppercase tracking-wider text-red-300 font-semibold mb-0.5">PPECB validation errors</p>
          {errorRows.map((v, i) => <ErrorLine key={i} v={v} named={named} canOpenPallet={canOpenPallet} />)}
        </div>
      )}
    </>
  )
}
